import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  computeStats,
  displayTitle,
  formatDecimal,
  formatDuration,
  formatHours,
  formatNumber,
  formatPercent,
  genreName,
  recommend,
  surpriseMe,
  type MonthBucket,
} from '@physical-movies/core';
import { useCollection, useI18n } from '../app/context';
import { MonthColumns, RankBars, ShelfStrip, type RankItem } from '../components/Charts';
import { PageHeader, Section } from '../components/Page';
import { RecommendationCard } from '../components/Recommendation';
import { Button, EmptyState } from '../components/ui';
import { useScrollToTop } from '../lib/useScrollRestoration';
import styles from './insights.module.css';

export function Insights() {
  useScrollToTop();
  const { t, locale } = useI18n();
  const { movies, watches } = useCollection();
  const navigate = useNavigate();

  const stats = useMemo(() => computeStats(movies, watches), [movies, watches]);

  const dust = useMemo(() => recommend(movies, watches, { limit: 12 }), [movies, watches]);
  const [pick, setPick] = useState(() => dust[0] ?? null);

  const describeMonth = (bucket: MonthBucket, monthLabel: string) =>
    `${monthLabel} — ${t('insights.watchesCounted', { count: bucket.watches })}${
      bucket.minutes > 0 ? `, ${formatDuration(bucket.minutes, t)}` : ''
    }`;

  const topMovies: RankItem[] = stats.topMovies.map((entry) => ({
    key: entry.movieId,
    label: entry.movie ? displayTitle(entry.movie, locale) : '—',
    value: entry.watches,
    valueLabel: `${entry.watches}× · ${formatDuration(entry.minutes, t)}`,
    ...(entry.movie ? { to: `/movie/${entry.movie.id}` } : {}),
  }));

  const ratingBars: RankItem[] = stats.ratingCounts
    .map((count, index) => ({
      key: `stars-${index + 1}`,
      label: t('insights.starsAxis', { count: index + 1 }),
      value: count,
      valueLabel: formatNumber(count, locale),
    }))
    .reverse();

  const genreBars: RankItem[] = stats.genres.slice(0, 8).map((bucket) => ({
    key: String(bucket.genreId),
    label: genreName(bucket.genreId, locale) ?? String(bucket.genreId),
    value: bucket.movies,
    valueLabel: formatNumber(bucket.movies, locale),
  }));

  if (movies.length === 0) {
    return (
      <div className={styles.page}>
        <PageHeader title={t('insights.title')} />
        <EmptyState
          icon="chart"
          title={t('insights.empty.title')}
          body={t('collection.empty.body')}
          action={
            <Button variant="primary" onClick={() => void navigate('/add')}>
              {t('collection.empty.action')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader title={t('insights.title')} />

      <Section title={t('rec.title')}>
        {pick ? (
          <div className={styles.tonight}>
            <RecommendationCard recommendation={pick} />
            <div className={styles.tonightActions}>
              <Button onClick={() => setPick(surpriseMe(dust))}>{t('rec.another')}</Button>
            </div>
          </div>
        ) : (
          <p className={styles.shelfNote}>{t('rec.empty.body')}</p>
        )}
      </Section>

      <Section title={t('insights.shelf')} aside={t('insights.shelfHint')}>
        <ShelfStrip counts={stats.formatCounts} />
        <p className={styles.shelfNote}>
          {t('collection.summary', { count: stats.movieCount, discs: stats.discCount })}
        </p>
      </Section>

      <Section title={t('insights.watchTime')}>
        <div className={styles.hero}>
          <span className={styles.heroValue}>{formatHours(stats.totalMinutes, locale, t)}</span>
          <span className={styles.heroLabel}>
            {stats.watchCount === 0
              ? t('insights.empty.body')
              : `${t('insights.stat.totalTime')} · ${t('insights.watchedShare', {
                  percent: formatPercent(stats.watchedShare, locale),
                })}`}
          </span>
        </div>

        <div className={styles.tiles}>
          <div className={styles.tile}>
            <span className={styles.tileValue}>
              {formatHours(stats.minutesThisYear, locale, t)}
            </span>
            <span className={styles.tileLabel}>{t('insights.stat.thisYear')}</span>
          </div>
          <div className={styles.tile}>
            <span className={styles.tileValue}>{formatNumber(stats.watchCount, locale)}</span>
            <span className={styles.tileLabel}>{t('insights.stat.watches')}</span>
          </div>
          <div className={styles.tile}>
            <span className={styles.tileValue}>
              {formatNumber(stats.unwatchedMovieCount, locale)}
            </span>
            <span className={styles.tileLabel}>{t('insights.stat.unwatched')}</span>
          </div>
          <div className={styles.tile}>
            <span className={styles.tileValue}>
              {stats.averageRating === null ? '—' : formatDecimal(stats.averageRating, locale)}
            </span>
            <span className={styles.tileLabel}>{t('insights.stat.avgRating')}</span>
          </div>
        </div>
      </Section>

      <Section
        title={t('insights.perMonth')}
        aside={
          stats.busiestMonth
            ? t('insights.busiestMonth', {
                month: new Intl.DateTimeFormat(locale === 'de' ? 'de-DE' : 'en-GB', {
                  month: 'long',
                  year: 'numeric',
                }).format(
                  new Date(
                    Number(stats.busiestMonth.month.slice(0, 4)),
                    Number(stats.busiestMonth.month.slice(5, 7)) - 1,
                    1,
                  ),
                ),
              })
            : undefined
        }
      >
        <MonthColumns buckets={stats.perMonth} describe={describeMonth} />
      </Section>

      <Section title={t('insights.topMovies')}>
        <RankBars items={topMovies} empty={t('watch.empty')} />
      </Section>

      <Section title={t('insights.ratings')}>
        <RankBars items={ratingBars} empty={t('insights.noRatings')} />
      </Section>

      <Section title={t('insights.genres')}>
        <RankBars items={genreBars} empty={t('common.none')} />
      </Section>

      {dust.length > 1 && (
        <Section title={t('rec.listTitle')}>
          <div className={styles.dustList}>
            {dust.slice(0, 4).map((entry) => (
              <RecommendationCard key={entry.movie.id} recommendation={entry} />
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
